// Every request/response shape the console relies on, as zod schemas (zod 4), matching the REAL
// services: crates/api (src/dto.rs, src/error.rs) and crates/region-api (src/lib.rs, which returns
// crates/query/src/shape.rs results verbatim). One file to diff when a contract moves.
import { z } from "zod";
import type { QueryFilterOperator, QueryIr, QueryResponse } from "./ir";

const id = z.string().min(1);
const datetime = z.iso.datetime({ offset: true });
const nullableDatetime = datetime.nullable();

export const REGION_CODES = ["eu", "me", "us", "pk"] as const;
export const ENVIRONMENTS = ["production", "preview", "development"] as const;

export const RegionCode = z.enum(REGION_CODES);
export type RegionCode = z.infer<typeof RegionCode>;
export const EnvironmentName = z.enum(ENVIRONMENTS);
export type EnvironmentName = z.infer<typeof EnvironmentName>;

// ── Errors ───────────────────────────────────────────────────────────────────
/**
 * Error envelope of both APIs: `{"error": {"code", "message"}}`. The control plane has no per-field
 * errors — validation failures are `code: "validation"` with a message that starts with the field
 * name (see `fieldsFor` in http.ts). region-api adds `path` (JSON Pointer into the IR) to `invalid_query`.
 */
export const ApiErrorBody = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    path: z.string().optional(),
    /** 1-based position into submitted SQL, on AQL compile errors. */
    position: z.object({ line: z.number().int(), column: z.number().int() }).optional(),
  }),
});
export type ApiErrorBody = z.infer<typeof ApiErrorBody>;

// ── Auth / me ────────────────────────────────────────────────────────────────
export const User = z.object({
  id,
  email: z.email(),
  name: z.string(),
  email_verified: z.boolean(),
  created_at: datetime,
});
export type User = z.infer<typeof User>;

export const OrgRole = z.enum(["owner", "admin", "member", "viewer"]);
export const OrgSummary = z.object({ id, name: z.string(), slug: z.string(), role: OrgRole, created_at: datetime });
export type OrgSummary = z.infer<typeof OrgSummary>;

/** GET /app/me (401 when signed out) and the body of signup/login. */
export const Me = z.object({ user: User, orgs: z.array(OrgSummary) });
export type Me = z.infer<typeof Me>;

export const SignUpInput = z.object({
  name: z.string().trim().min(1, "Enter your name").max(100),
  email: z.email("Enter a valid email address"),
  password: z.string().min(10, "Use at least 10 characters").max(200),
});
export type SignUpInput = z.infer<typeof SignUpInput>;

export const SignInInput = z.object({
  email: z.email("Enter a valid email address"),
  password: z.string().min(1, "Enter your password"),
});
export type SignInInput = z.infer<typeof SignInInput>;

// ── Orgs ─────────────────────────────────────────────────────────────────────
/** POST /app/orgs → the org, with your role in it. */
export const Org = OrgSummary;
export type Org = z.infer<typeof Org>;

export const CreateOrgInput = z.object({
  name: z.string().trim().min(2, "Use at least 2 characters").max(100),
});
export type CreateOrgInput = z.infer<typeof CreateOrgInput>;

// ── Regions ──────────────────────────────────────────────────────────────────
/** GET /app/regions — the region registry (core.regions). */
export const Region = z.object({
  code: RegionCode,
  name: z.string(),
  status: z.enum(["live", "coming_soon"]),
  api_host: z.string().nullable(),
  ingest_host: z.string().nullable(),
});
export type Region = z.infer<typeof Region>;

// ── Projects, environments, keys ─────────────────────────────────────────────
export const Environment = z.object({ id, name: EnvironmentName, is_default: z.boolean() });
export type Environment = z.infer<typeof Environment>;

export const Project = z.object({
  id,
  /** Numeric id the data plane uses (ClickHouse `project_id`). */
  data_id: z.number().int(),
  org_id: id,
  name: z.string(),
  slug: z.string(),
  region: RegionCode,
  timezone: z.string(),
  currency: z.string().length(3),
  retention_days: z.number().int(),
  created_at: datetime,
  /** Your effective role in this project. */
  role: z.string(),
  environments: z.array(Environment),
});
export type Project = z.infer<typeof Project>;

export const CreateProjectInput = z.object({
  name: z.string().trim().min(2, "Use at least 2 characters").max(100),
  region: RegionCode,
  timezone: z.string().min(1),
});
export type CreateProjectInput = z.infer<typeof CreateProjectInput>;

export const PUBLIC_KEY_RE = /^atx_pk_(eu|me|us|pk)_(production|preview|development)_[A-Za-z0-9]{22}$/;

export const PublicKey = z.object({
  id,
  project_id: id,
  environment: EnvironmentName,
  key: z.string().regex(PUBLIC_KEY_RE),
  allowed_origins: z.array(z.string()),
  allowed_bundle_ids: z.array(z.string()),
  created_at: datetime,
  revoked_at: nullableDatetime,
  /** Whether the region's capture service already has this key's latest state. */
  registry_synced: z.boolean(),
});
export type PublicKey = z.infer<typeof PublicKey>;

/** POST /app/orgs/{org}/projects — creating a project mints one public key per environment. */
export const CreateProjectResponse = z.object({ project: Project, public_keys: z.array(PublicKey) });
export type CreateProjectResponse = z.infer<typeof CreateProjectResponse>;

/**
 * POST /app/projects/{id}/keys/{key}/rotate `{keep_old?}` — a replacement for the same environment.
 * With `keep_old` the old key keeps working and `revoked.revoked_at` is still null.
 */
export const RotateKeyResponse = z.object({ revoked: PublicKey, created: PublicKey });
export type RotateKeyResponse = z.infer<typeof RotateKeyResponse>;

// ── Personal access tokens ───────────────────────────────────────────────────
export const SCOPE_DOMAINS = [
  "query",
  "insights",
  "dashboards",
  "cohorts",
  "catalog",
  "flags",
  "experiments",
  "replays",
  "persons",
  "alerts",
  "ai",
  "annotations",
  "usage",
] as const;
/** `domain:read` / `domain:write`, or a named scope (`sql.raw`, …). */
export const Scope = z.string().regex(/^[a-z_]+([.:][a-z_]+)*$/);

export const PersonalToken = z.object({
  id,
  kind: z.string(),
  org_id: id,
  name: z.string(),
  /** First chars of the token, safe to display: `atx_pat_` + 12-char lookup prefix. */
  prefix: z.string(),
  scopes: z.array(Scope),
  /** null = every project in the org. */
  project_ids: z.array(id).nullable(),
  created_at: datetime,
  expires_at: nullableDatetime,
  last_used_at: nullableDatetime,
  revoked_at: nullableDatetime,
});
export type PersonalToken = z.infer<typeof PersonalToken>;

export const CreateTokenInput = z.object({
  name: z.string().trim().min(1, "Name the token").max(100),
  scopes: z.array(Scope).min(1, "Pick at least one scope"),
  expires_in_days: z.number().int().positive().max(365).nullable(),
  org_id: id,
});
export type CreateTokenInput = z.infer<typeof CreateTokenInput>;

/** The secret is returned exactly once, on creation. */
export const CreatedToken = z.object({
  token: PersonalToken,
  secret: z.string().regex(/^atx_pat_[A-Za-z0-9]{44}$/),
});
export type CreatedToken = z.infer<typeof CreatedToken>;

// ── Region token ─────────────────────────────────────────────────────────────
/** POST /app/projects/{id}/region-token `{environment?, scopes?}` — a ≤15 min EdDSA JWT for the region API. */
export const RegionToken = z.object({
  token: z.string().min(1),
  region_api_url: z.url(),
  expires_at: datetime,
  region: RegionCode,
  environment: EnvironmentName,
  scopes: z.array(z.string()),
});
export type RegionToken = z.infer<typeof RegionToken>;

// ── Region API: schema ───────────────────────────────────────────────────────
/** GET {region}/schema/events[?days=] — event names with volume (event_counts_daily MV, bots excluded). */
export const EventsWire = z.object({
  days: z.number().int(),
  events: z.array(z.object({ event: z.string(), count: z.number(), last_seen: z.string() })),
  cached: z.boolean(),
});
export type EventsWire = z.infer<typeof EventsWire>;

const WireType = z.enum(["String", "Numeric", "Boolean", "Null", "Object"]);
/** GET {region}/schema/properties[?event=&type=event|person] — keys with an inferred JSON type. */
export const PropertiesWire = z.object({
  event: z.string().nullable(),
  type: z.enum(["event", "person"]),
  sample: z.number().int(),
  properties: z.array(z.object({ key: z.string(), type: WireType, count: z.number() })),
  cached: z.boolean(),
});
export type PropertiesWire = z.infer<typeof PropertiesWire>;

/** Console view of an event name. */
export interface EventDefinition {
  name: string;
  /** Volume over the schema window (30 days). */
  volume_30d: number;
  /** Last day seen (`YYYY-MM-DD`). */
  last_seen: string | null;
  /** `$`-prefixed events are captured automatically by the SDKs. */
  is_system: boolean;
}

export type PropertyType = "string" | "number" | "boolean" | "object" | "null";
/** Console view of a property key. */
export interface PropertyDefinition {
  name: string;
  type: PropertyType;
  scope: "event" | "person";
  is_system: boolean;
}

export const toEventDefinitions = (w: EventsWire): EventDefinition[] =>
  w.events.map((e) => ({
    name: e.event,
    volume_30d: e.count,
    last_seen: e.last_seen || null,
    is_system: e.event.startsWith("$"),
  }));

const PROPERTY_TYPE: Record<z.infer<typeof WireType>, PropertyType> = {
  String: "string",
  Numeric: "number",
  Boolean: "boolean",
  Object: "object",
  Null: "null",
};

export const toPropertyDefinitions = (w: PropertiesWire): PropertyDefinition[] =>
  w.properties.map((p) => ({
    name: p.key,
    type: PROPERTY_TYPE[p.type],
    scope: w.type,
    is_system: p.key.startsWith("$"),
  }));

// ── Region API: query results (ir.ts result types / crates/query shape.rs) ───
const num = z.number();
const maybe = num.nullable();
const funnelStep = z.object({
  index: z.number().int(),
  event: z.string(),
  label: z.string(),
  count: num,
  conversion_from_first: maybe,
  conversion_from_previous: maybe,
  drop_off: num,
  drop_off_rate: maybe,
});
const period = z.enum(["day", "week", "month"]);

export const QueryResult = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("trends"),
    interval: z.enum(["hour", "day", "week", "month"]),
    timezone: z.string(),
    labels: z.array(z.string()),
    previous_labels: z.array(z.string()).nullable(),
    series: z.array(
      z.object({
        letter: z.string(),
        label: z.string(),
        event: z.string().nullable(),
        math: z.string(),
        breakdown_value: z.string().nullable(),
        compare: z.string().nullable(),
        data: z.array(maybe),
        total: num,
      }),
    ),
    sampling: maybe,
    fast_path: z.boolean(),
  }),
  z.object({
    kind: z.literal("funnel"),
    steps: z.array(funnelStep),
    breakdowns: z.array(z.object({ breakdown_value: z.string(), steps: z.array(funnelStep) })).optional(),
    conversion_window_seconds: num,
    sampling: maybe,
    time_to_convert: z
      .object({
        from_step: z.number().int(),
        to_step: z.number().int(),
        conversions: num,
        average_seconds: maybe,
        median_seconds: maybe,
        p90_seconds: maybe,
        sampled: z.boolean(),
        bins: z.array(z.object({ from_seconds: num, to_seconds: num, count: num })),
      })
      .optional(),
  }),
  z.object({
    kind: z.literal("retention"),
    period,
    periods: z.number().int(),
    retention_type: z.enum(["first_time", "recurring"]),
    cohorts: z.array(z.object({ cohort: z.string(), size: num, values: z.array(maybe), percentages: z.array(maybe) })),
    sampling: maybe,
  }),
  z.object({
    kind: z.literal("paths"),
    node_type: z.enum(["pageview", "screen", "event"]),
    edges: z.array(z.object({ step: z.number().int(), source: z.string(), target: z.string(), value: num })),
    nodes: z.array(z.object({ step: z.number().int(), name: z.string(), outgoing: num })),
    sampling: maybe,
  }),
  z.object({
    kind: z.literal("lifecycle"),
    interval: period,
    labels: z.array(z.string()),
    series: z.array(z.object({ status: z.enum(["new", "returning", "resurrecting", "dormant"]), data: z.array(num) })),
    sampling: maybe,
  }),
  z.object({
    kind: z.literal("stickiness"),
    interval: period,
    labels: z.array(z.string()),
    series: z.array(
      z.object({ letter: z.string(), label: z.string(), event: z.string().nullable(), data: z.array(num), total: num }),
    ),
    sampling: maybe,
  }),
]);

export const QueryMeta = z.object({
  cached: z.boolean(),
  query_fingerprint: z.string(),
  data_version: z.string(),
  date_from: z.string(),
  date_to: z.string(),
  executed_at: z.string(),
  rows_read: num,
  bytes_read: num,
  elapsed_ms: num,
  compiler_version: z.union([z.string(), num]),
});

/** POST {region}/query body `{ query: QueryIr }` → this. */
export const QueryResponseSchema = z.object({
  kind: z.enum(["trends", "funnel", "retention", "paths", "lifecycle", "stickiness"]),
  result: QueryResult,
  meta: QueryMeta,
  compiled: z.object({ sql: z.string(), params: z.unknown() }).optional(),
}) as unknown as z.ZodType<QueryResponse>;

// ══ Wave 2 ═══════════════════════════════════════════════════════════════════
// The console's VIEW types for account security, saved insights, dashboards, cohorts, catalog, people
// and SQL. The wire shapes of the real endpoints (and the mapping into these types) live in wire.ts;
// the fixture adapter produces these types directly. Comments name the endpoint each view comes from.

const userRef = z.object({ id, name: z.string(), email: z.string() }).nullable();
export type UserRef = z.infer<typeof userRef>;
const count = z.number().int().nonnegative();
/** JSON object of arbitrary properties (person/event/group properties are customer data). */
const props = z.record(z.string(), z.unknown());

// ── Account ──────────────────────────────────────────────────────────────────
export const NewPassword = z.string().min(10, "Use at least 10 characters").max(200);

/**
 * POST /app/auth/change-password → 204 (crates/api routes/account.rs). Signs out every other session.
 * Errors: 400 `invalid_current_password` | `weak_password` | `no_password` (OAuth-only account).
 */
export const ChangePasswordInput = z.object({
  current_password: z.string().min(1, "Enter your current password"),
  new_password: NewPassword,
});
export type ChangePasswordInput = z.infer<typeof ChangePasswordInput>;

/** POST /app/auth/password-reset → 202 `{status: "accepted"}`, whether or not the email has an account. */
export const ForgotPasswordInput = z.object({ email: z.email("Enter a valid email address") });
export type ForgotPasswordInput = z.infer<typeof ForgotPasswordInput>;
export const AcceptedResponse = z.object({ status: z.literal("accepted") });

/**
 * POST /app/auth/password-reset/confirm → 204. The token comes from the emailed link
 * (/reset-password?token=…); every session is revoked. Errors: 400 `invalid_token` | `weak_password`.
 */
export const ResetPasswordInput = z.object({ token: z.string().min(1), new_password: NewPassword });
export type ResetPasswordInput = z.infer<typeof ResetPasswordInput>;

// POST /app/auth/verify-email {token} → 204 (404 for an unknown/used/expired token); the emailed link
// is /verify-email?token=…. POST /app/auth/verify-email/send → 204 re-sends it (429 when asked too often).

/** GET /app/auth/sessions → { sessions }. `location` is coarse (city, country), never an IP. */
export const Session = z.object({
  id,
  current: z.boolean(),
  user_agent: z.string().nullable(),
  location: z.string().nullable(),
  created_at: datetime,
  last_seen_at: datetime,
  expires_at: datetime,
});
export type Session = z.infer<typeof Session>;
// DELETE /app/auth/sessions/{id} → 204; POST /app/auth/sessions/revoke-others → { revoked }.
export const RevokeOthersResponse = z.object({ revoked: count });

// ── OAuth connections (GET /app/oauth/connections, DELETE …/{grant_id} → 204) ──
/** An app the user approved over OAuth (an MCP client such as Claude Code). `id` is the grant id. */
export const OAuthConnection = z.object({
  id,
  client_name: z.string(),
  client_id: z.string(),
  org_id: id,
  scopes: z.array(z.string()),
  created_at: datetime,
  last_used_at: nullableDatetime,
});
export type OAuthConnection = z.infer<typeof OAuthConnection>;

// ── Members (REAL: GET /app/orgs/{org}/members) ──────────────────────────────
export const Member = z.object({
  id,
  user_id: id,
  email: z.string(),
  name: z.string(),
  role: z.string(),
  created_at: datetime,
});
export type Member = z.infer<typeof Member>;
export const MembersResponse = z.object({ members: z.array(Member), invitations: z.array(z.unknown()) });

// ── Saved insights (def.insights) ────────────────────────────────────────────
const queryIr = z.custom<QueryIr>(
  (v) => typeof v === "object" && v !== null && "insight" in v && "date_range" in v,
  "Expected a Query IR",
);
export const INSIGHT_KINDS = ["trends", "funnel", "retention", "paths"] as const;
export const SavedInsightKind = z.enum(INSIGHT_KINDS);
export type SavedInsightKind = z.infer<typeof SavedInsightKind>;
export const InsightViz = z.object({ display: z.enum(["line", "bar"]).optional() });
export type InsightViz = z.infer<typeof InsightViz>;
export const INSIGHT_TAG_RE = /^[a-z0-9][a-z0-9 _-]{0,31}$/;

/**
 * GET /app/projects/{p}/insights[?search=&kind=&tag=&favorites=1] → { insights },
 * GET /app/projects/{p}/insights/{id} → SavedInsight. `favorite` is per viewer; `tags` is a new column
 * (def.insights has none yet).
 */
export const SavedInsight = z.object({
  id,
  project_id: id,
  name: z.string(),
  description: z.string().nullable(),
  query_ir: queryIr,
  ir_version: z.number().int(),
  viz: InsightViz,
  tags: z.array(z.string()),
  favorite: z.boolean(),
  created_via: z.enum(["ui", "api", "mcp", "ai"]),
  created_by: userRef,
  created_at: datetime,
  updated_at: datetime,
});
export type SavedInsight = z.infer<typeof SavedInsight>;

export const InsightFields = z.object({
  name: z.string().trim().min(1, "Name the insight").max(200),
  description: z.string().trim().max(2000).nullable(),
  tags: z.array(z.string().trim().toLowerCase().regex(INSIGHT_TAG_RE, "Tags are short words: letters, numbers, - and _")).max(10),
});
/** POST /app/projects/{p}/insights → SavedInsight. */
export const CreateInsightInput = InsightFields.extend({ query_ir: queryIr, viz: InsightViz });
export type CreateInsightInput = z.infer<typeof CreateInsightInput>;
/** PATCH /app/projects/{p}/insights/{id} → SavedInsight; DELETE → 204 (soft delete). */
export const UpdateInsightInput = CreateInsightInput.partial();
export type UpdateInsightInput = z.infer<typeof UpdateInsightInput>;
// PUT /app/projects/{p}/insights/{id}/favorite { favorite } → SavedInsight.

export interface InsightListParams {
  search?: string;
  kind?: SavedInsightKind;
  tag?: string;
  favorites?: boolean;
}

// ── Dashboards (def.dashboards + def.dashboard_tiles) ────────────────────────
export const GRID_COLUMNS = 12;
/** Grid cell units: 12 columns, rows of a fixed height. */
export const TileLayout = z.object({
  x: z.number().int().min(0).max(GRID_COLUMNS - 1),
  y: z.number().int().min(0),
  w: z.number().int().min(1).max(GRID_COLUMNS),
  h: z.number().int().min(1).max(8),
});
export type TileLayout = z.infer<typeof TileLayout>;

export const DashboardTile = z.object({
  id,
  kind: z.enum(["insight", "text"]),
  insight_id: id.nullable(),
  /** The tile's insight, embedded so a dashboard renders from one request. */
  insight: SavedInsight.nullable(),
  /** Markdown-ish text for text tiles (rendered as plain paragraphs). */
  body: z.string().nullable(),
  layout: TileLayout,
  created_at: datetime,
});
export type DashboardTile = z.infer<typeof DashboardTile>;

/** Dashboard-level overrides applied to every tile's query. */
export const DashboardFilters = z.object({
  date_range: z.object({ from: z.string(), to: z.string().optional() }).optional(),
});
export type DashboardFilters = z.infer<typeof DashboardFilters>;

/** GET /app/projects/{p}/dashboards → { dashboards }. At most one per project is `pinned` (Home). */
export const DashboardSummary = z.object({
  id,
  project_id: id,
  name: z.string(),
  description: z.string().nullable(),
  pinned: z.boolean(),
  tile_count: count,
  created_by: userRef,
  created_at: datetime,
  updated_at: datetime,
});
export type DashboardSummary = z.infer<typeof DashboardSummary>;

/** GET /app/projects/{p}/dashboards/{id} → Dashboard. */
export const Dashboard = DashboardSummary.extend({ filters: DashboardFilters, tiles: z.array(DashboardTile) });
export type Dashboard = z.infer<typeof Dashboard>;

/** POST /app/projects/{p}/dashboards → Dashboard. */
export const CreateDashboardInput = z.object({
  name: z.string().trim().min(1, "Name the dashboard").max(200),
  description: z.string().trim().max(2000).nullable(),
});
export type CreateDashboardInput = z.infer<typeof CreateDashboardInput>;
/**
 * PATCH /app/projects/{p}/dashboards/{id} → Dashboard; DELETE → 204.
 * `pinned: true` makes it the project's Home and unpins any other.
 */
export const UpdateDashboardInput = CreateDashboardInput.partial().extend({
  pinned: z.boolean().optional(),
  filters: DashboardFilters.optional(),
});
export type UpdateDashboardInput = z.infer<typeof UpdateDashboardInput>;
/** PUT /app/projects/{p}/dashboards/{id}/layout → Dashboard. Every tile, new positions. */
export const DashboardLayoutInput = z.object({ tiles: z.array(z.object({ id, layout: TileLayout })) });
export type DashboardLayoutInput = z.infer<typeof DashboardLayoutInput>;
/** POST /app/projects/{p}/dashboards/{id}/tiles → DashboardTile. */
export const CreateTileInput = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("insight"), insight_id: id, layout: TileLayout.optional() }),
  z.object({ kind: z.literal("text"), body: z.string().trim().min(1, "Write something").max(4000), layout: TileLayout.optional() }),
]);
export type CreateTileInput = z.infer<typeof CreateTileInput>;
// PATCH /app/projects/{p}/dashboards/{id}/tiles/{tile} { body } → DashboardTile; DELETE → 204.

// ── Cohorts (def.cohorts; membership in the region) ──────────────────────────
export const FILTER_OPERATORS = [
  "eq",
  "neq",
  "contains",
  "not_contains",
  "gt",
  "lt",
  "is_set",
  "is_not_set",
] as const satisfies readonly QueryFilterOperator[];
const filterScalar = z.union([z.string(), z.number(), z.boolean()]);

/** "Did `event` at least/at most/exactly `count` times in the last `days` days". */
export const BehavioralCriterion = z.object({
  type: z.literal("behavioral"),
  event: z.string().min(1, "Pick an event"),
  operator: z.enum(["gte", "lte", "eq"]),
  count: z.number().int().min(0).max(100_000),
  days: z.number().int().min(1).max(365),
});
/** A person property condition, with the Query IR's operator vocabulary. */
export const PropertyCriterion = z.object({
  type: z.literal("person_property"),
  key: z.string().min(1, "Pick a property"),
  operator: z.enum(FILTER_OPERATORS),
  value: filterScalar.optional(),
});
export const CohortCriterion = z.discriminatedUnion("type", [BehavioralCriterion, PropertyCriterion]);
export type CohortCriterion = z.infer<typeof CohortCriterion>;
export const CohortDefinition = z.object({
  match: z.enum(["all", "any"]),
  criteria: z.array(CohortCriterion).min(1, "Add at least one condition").max(20),
});
export type CohortDefinition = z.infer<typeof CohortDefinition>;

/** GET /app/projects/{p}/cohorts → { cohorts }; GET …/cohorts/{id} → Cohort. */
export const Cohort = z.object({
  id,
  project_id: id,
  name: z.string(),
  description: z.string().nullable(),
  kind: z.enum(["dynamic", "static"]),
  definition: CohortDefinition.nullable(),
  version: z.number().int(),
  last_calculated_at: nullableDatetime,
  created_by: userRef,
  created_at: datetime,
  updated_at: datetime,
});
export type Cohort = z.infer<typeof Cohort>;

/** POST /app/projects/{p}/cohorts → Cohort. A static cohort has no definition. */
export const CreateCohortInput = z
  .object({
    name: z.string().trim().min(1, "Name the cohort").max(200),
    description: z.string().trim().max(2000).nullable(),
    kind: z.enum(["dynamic", "static"]),
    definition: CohortDefinition.nullable(),
  })
  .refine((c) => (c.kind === "dynamic") === (c.definition !== null), {
    message: "A dynamic cohort needs conditions; a static one has none",
    path: ["definition"],
  });
export type CreateCohortInput = z.infer<typeof CreateCohortInput>;
/** PATCH /app/projects/{p}/cohorts/{id} → Cohort (a new definition bumps `version`); DELETE → 204. */
export const UpdateCohortInput = z.object({
  name: z.string().trim().min(1, "Name the cohort").max(200).optional(),
  description: z.string().trim().max(2000).nullable().optional(),
  definition: CohortDefinition.optional(),
});
export type UpdateCohortInput = z.infer<typeof UpdateCohortInput>;

/** POST {region}/cohorts/preview { definition } → CohortSize (not saved). */
export const CohortSize = z.object({
  count,
  /** Persons in the project, for "N of M people" (null when the region can't say). */
  total: count.nullable(),
  calculated_at: datetime,
});
export type CohortSize = z.infer<typeof CohortSize>;
// GET {region}/cohorts/{id}/size → CohortSize (latest computed version).

export const MAX_STATIC_UPLOAD = 100_000;
/** POST {region}/cohorts/{id}/members { distinct_ids } → StaticUploadResult (static cohorts; replaces members). */
export const StaticUploadResult = z.object({ matched: count, unmatched: count, members: count });
export type StaticUploadResult = z.infer<typeof StaticUploadResult>;

// ── Catalog (def.catalog_events / def.catalog_properties) ────────────────────
export const CATALOG_STATUSES = ["planned", "implemented", "verified", "deprecated", "unplanned"] as const;
export const CatalogStatus = z.enum(CATALOG_STATUSES);
export type CatalogStatus = z.infer<typeof CatalogStatus>;
export const PII_LEVELS = ["none", "pseudonymous", "personal", "sensitive"] as const;
export const PiiLevel = z.enum(PII_LEVELS);
export type PiiLevel = z.infer<typeof PiiLevel>;

/** GET /app/projects/{p}/catalog/events → { events }. Observed volume comes from the region. */
export const CatalogEvent = z.object({
  id,
  project_id: id,
  name: z.string(),
  display_name: z.string().nullable(),
  description: z.string().nullable(),
  question: z.string().nullable(),
  status: CatalogStatus,
  owner: userRef,
  platforms: z.array(z.string()),
  tags: z.array(z.string()),
  pii_level: PiiLevel,
  first_seen_at: nullableDatetime,
  last_seen_at: nullableDatetime,
  updated_at: datetime,
});
export type CatalogEvent = z.infer<typeof CatalogEvent>;

const catalogText = z.string().trim().max(2000).nullable();
/** PATCH /app/projects/{p}/catalog/events/{id} → CatalogEvent. */
export const UpdateCatalogEventInput = z.object({
  description: catalogText.optional(),
  question: catalogText.optional(),
  owner_id: id.nullable().optional(),
  pii_level: PiiLevel.optional(),
  status: CatalogStatus.optional(),
});
export type UpdateCatalogEventInput = z.infer<typeof UpdateCatalogEventInput>;
/** POST /app/projects/{p}/catalog/events → CatalogEvent (e.g. adopting an unplanned event). */
export const CreateCatalogEventInput = UpdateCatalogEventInput.extend({ name: z.string().min(1).max(200) });
export type CreateCatalogEventInput = z.infer<typeof CreateCatalogEventInput>;

export const CatalogPropertyType = z.enum(["string", "number", "bool", "datetime", "money_minor", "currency", "enum", "id"]);
/** GET /app/projects/{p}/catalog/properties → { properties }. */
export const CatalogProperty = z.object({
  id,
  project_id: id,
  scope: z.enum(["event", "person", "group", "session"]),
  /** For event scope: the event it belongs to; null = shared by every event. */
  event_name: z.string().nullable(),
  name: z.string(),
  type: CatalogPropertyType,
  pii: PiiLevel,
  description: z.string().nullable(),
  required: z.boolean(),
  updated_at: datetime,
});
export type CatalogProperty = z.infer<typeof CatalogProperty>;
/** PATCH /app/projects/{p}/catalog/properties/{id} → CatalogProperty. */
export const UpdateCatalogPropertyInput = z.object({ description: catalogText.optional(), pii: PiiLevel.optional() });
export type UpdateCatalogPropertyInput = z.infer<typeof UpdateCatalogPropertyInput>;

/**
 * GET {region}/events/live[?event=] as Server-Sent Events (`text/event-stream`,
 * bearer region token, `event: event`, one LiveEvent JSON per `data:`). Bots excluded, properties trimmed.
 */
export const LiveEvent = z.object({
  uuid: z.string(),
  event: z.string(),
  distinct_id: z.string(),
  timestamp: z.string(),
  platform: z.string().nullable(),
  properties: props,
});
export type LiveEvent = z.infer<typeof LiveEvent>;

// ── People & groups (regional persons / identity tables) ─────────────────────
/** GET {region}/persons[?search=&cursor=&limit=] → PersonsPage. Search matches distinct ids and email/name. */
export const PersonSummary = z.object({
  id,
  /** Best display name from $name / name / email properties, else null. */
  name: z.string().nullable(),
  email: z.string().nullable(),
  distinct_ids: z.array(z.string()),
  distinct_id_count: count,
  is_identified: z.boolean(),
  properties: props,
  created_at: datetime,
  last_seen_at: nullableDatetime,
});
export type PersonSummary = z.infer<typeof PersonSummary>;
export const PersonsPage = z.object({ persons: z.array(PersonSummary), next_cursor: z.string().nullable() });
export type PersonsPage = z.infer<typeof PersonsPage>;

/** GET {region}/persons/{id} → Person (404 across tenants, and for erased persons). */
export const Person = PersonSummary.extend({ updated_at: datetime, merged_into: id.nullable() });
export type Person = z.infer<typeof Person>;

/** GET {region}/persons/{id}/events[?before=&limit=] → { events, next_cursor }, newest first. */
export const PersonEvent = z.object({
  uuid: z.string(),
  event: z.string(),
  timestamp: z.string(),
  distinct_id: z.string(),
  properties: props,
});
export type PersonEvent = z.infer<typeof PersonEvent>;
export const PersonEventsPage = z.object({ events: z.array(PersonEvent), next_cursor: z.string().nullable() });
export type PersonEventsPage = z.infer<typeof PersonEventsPage>;

export const IdentityEdge = z.object({
  id: z.number().int(),
  from_distinct_id: z.string(),
  to_distinct_id: z.string(),
  /** identify | alias | merge_dangerously | revenue_alias | group */
  kind: z.string(),
  /** What the engine did with the edge: absorbed | merged | noop | refused | blocked | capped. */
  outcome: z.string(),
  source_event_uuid: z.string().nullable(),
  created_at: datetime,
  reverted_at: nullableDatetime,
  reverted_by: z.string().nullable(),
  /** Why it was reverted, for the audit log. */
  reason: z.string().nullable(),
  /** The region says this edge can be undone (live, and it joined two persons). */
  unmergeable: z.boolean(),
});
export type IdentityEdge = z.infer<typeof IdentityEdge>;
export const IdentityAudit = z.object({
  id: z.number().int(),
  /** create | merge | split | alias | revert | property_set | erase | absorb | refuse | block | cap | unmerge */
  kind: z.string(),
  actor: z.string(),
  reason: z.string().nullable(),
  edge_id: z.number().int().nullable(),
  created_at: datetime,
  before_snapshot: z.unknown(),
  after_snapshot: z.unknown(),
});
export type IdentityAudit = z.infer<typeof IdentityAudit>;
/** GET {region}/persons/{id}/identity → IdentityGraph (the person's component, reverted edges included). */
export const IdentityGraph = z.object({
  person_id: id,
  /** `member` = belongs to this person; others are counterparts of refused/blocked/undone links. */
  nodes: z.array(z.object({ distinct_id: z.string(), is_identified: z.boolean(), member: z.boolean(), blocked: z.boolean() })),
  edges: z.array(IdentityEdge),
  audit: z.array(IdentityAudit),
});
export type IdentityGraph = z.infer<typeof IdentityGraph>;

/**
 * POST {region}/persons/{id}/unmerge { edge_id, reason } → UnmergeResult.
 * Needs `persons:write`. Reverts the edge, recomputes the component and restores the pre-merge snapshot.
 */
export const UnmergeInput = z.object({
  edge_id: z.number().int(),
  reason: z.string().trim().min(3, "Say why, for the audit log").max(500),
});
export type UnmergeInput = z.infer<typeof UnmergeInput>;
export const UnmergeResult = z.object({ edge_id: z.number().int(), split_person_ids: z.array(id) });
export type UnmergeResult = z.infer<typeof UnmergeResult>;

/** GET {region}/groups/types → { types }. */
export const GroupType = z.object({
  index: z.number().int().min(0).max(4),
  group_type: z.string(),
  name_singular: z.string().nullable(),
  name_plural: z.string().nullable(),
});
export type GroupType = z.infer<typeof GroupType>;
/** GET {region}/groups?type_index=[&search=&cursor=] → GroupsPage. */
export const Group = z.object({
  group_type_index: z.number().int(),
  group_key: z.string(),
  properties: props,
  created_at: datetime,
  updated_at: datetime,
});
export type Group = z.infer<typeof Group>;
export const GroupsPage = z.object({ groups: z.array(Group), next_cursor: z.string().nullable() });
export type GroupsPage = z.infer<typeof GroupsPage>;

// ── SQL (AQL, gated by the `sql.raw` scope on the region token) ──────────────
export const SQL_ROW_LIMIT = 10_000;
/** POST {region}/sql { sql, limit? } → SqlResult. */
export const SqlInput = z.object({
  sql: z.string().trim().min(1, "Write a query").max(100_000),
  limit: z.number().int().min(1).max(SQL_ROW_LIMIT).optional(),
});
export type SqlInput = z.infer<typeof SqlInput>;
export const SqlResult = z.object({
  columns: z.array(z.object({ name: z.string(), type: z.string() })),
  rows: z.array(z.array(z.unknown())),
  /** More rows matched than `limit`. */
  truncated: z.boolean(),
  meta: z.object({ rows_read: z.number(), bytes_read: z.number(), elapsed_ms: z.number() }),
});
export type SqlResult = z.infer<typeof SqlResult>;
/**
 * SQL errors reuse the error envelope with code `sql_invalid` | `sql_forbidden`
 * | `sql_timeout` and an optional 1-based `position` ({ line, column }) into the submitted SQL.
 */
export const SqlErrorPosition = z.object({ line: z.number().int().min(1), column: z.number().int().min(1) });
export type SqlErrorPosition = z.infer<typeof SqlErrorPosition>;
export const SQL_SCOPE = "sql.raw";

// ══ Wave 3 ═══════════════════════════════════════════════════════════════════
// Ask (region, ai-answer.schema.json), replays (region, crates/region-api/src/replays.rs), metrics and
// annotations (control plane, defs-metric / defs-annotation), flags and experiments (control plane —
// `SHAPE: from flags branch`: copied from worktree-agent-a65cdf38957e2fb7e's contracts, not yet on main).

const jsonObject = z.record(z.string(), z.unknown());

// ── Metrics + annotations (forwarded to Ask as `definitions`) ────────────────
/** GET /app/projects/{p}/metrics → { metrics } (defs-metric.schema.json). */
export const Metric = z.object({
  id,
  project_id: id,
  key: z.string(),
  version: z.number().int(),
  name: z.string(),
  description: z.string().nullable().optional(),
  kind: z.enum(["trends", "funnel", "retention", "paths", "lifecycle", "stickiness"]),
  query: queryIr,
  unit: z.string().nullable(),
  direction: z.enum(["increase", "decrease", "neutral"]),
  owner_id: id.nullable(),
  certified: z.boolean(),
  certified_by: id.nullable(),
  certified_at: nullableDatetime,
  monitor: z.boolean(),
  created_at: datetime,
  archived_at: nullableDatetime,
});
export type Metric = z.infer<typeof Metric>;
export const MetricListResponse = z.object({ metrics: z.array(Metric) });
/** POST /app/projects/{p}/metrics → 201 Metric (MetricCreateRequest). 409 when the key exists. */
export const CreateMetricInput = z.object({
  key: z.string().regex(/^[a-z][a-z0-9_]{0,99}$/, "Lowercase letters, numbers and underscores, starting with a letter"),
  name: z.string().trim().min(1).max(200),
  query: queryIr,
  unit: z.string().max(16).nullable().optional(),
  direction: z.enum(["increase", "decrease", "neutral"]).optional(),
});
export type CreateMetricInput = z.infer<typeof CreateMetricInput>;

export const ANNOTATION_KINDS = ["release", "incident", "marketing", "flag_change", "experiment"] as const;
/** GET /app/projects/{p}/annotations[?from=&to=&kind=] → { annotations } (defs-annotation.schema.json). */
export const Annotation = z.object({
  id,
  project_id: id,
  kind: z.enum(ANNOTATION_KINDS),
  source: z.string(),
  content: z.string(),
  occurred_at: datetime,
  ref: jsonObject.nullable(),
  created_by: id.nullable(),
  created_at: datetime,
});
export type Annotation = z.infer<typeof Annotation>;
export const AnnotationListResponse = z.object({ annotations: z.array(Annotation) });

// ── Ask (region: POST /ask SSE, GET /ask/conversations[/{id}]; scope ai:read) ─
export const AI_SCOPE = "ai:read";

/** `AiDefinitions`: the project's configuration the console forwards so the region needn't call home. */
export const AskDefinitions = z.object({
  events: z.array(z.object({ name: z.string().min(1).max(200), status: CatalogStatus })).max(5000),
  metrics: z
    .array(z.object({ key: z.string(), name: z.string().max(200), certified: z.boolean(), definition: jsonObject }))
    .max(500),
  annotations: z
    .array(z.object({ kind: z.enum(ANNOTATION_KINDS), content: z.string().max(1000), occurred_at: datetime }))
    .max(1000),
});
export type AskDefinitions = z.infer<typeof AskDefinitions>;

/** `AiAskRequest`. */
export const AskInput = z.object({
  question: z.string().trim().min(1, "Ask a question").max(4000, "Keep the question under 4,000 characters"),
  conversation_id: id.optional(),
  definitions: AskDefinitions.optional(),
});
export type AskInput = z.infer<typeof AskInput>;

const evidenceId = z.string().regex(/^e[0-9]{1,3}$/);
export const AiBinding = z.object({
  placeholder: z.string(),
  expr: z.string(),
  value: z.number().nullable(),
  formatted: z.string(),
});
export type AiBinding = z.infer<typeof AiBinding>;
export const AiClaim = z.object({
  id: z.string(),
  text: z.string(),
  template: z.string(),
  evidence_ids: z.array(evidenceId).min(1),
  bindings: z.array(AiBinding),
});
export type AiClaim = z.infer<typeof AiClaim>;
export const AI_CHART_TYPES = ["line", "bar", "funnel", "retention_grid", "lifecycle", "stickiness", "paths", "table", "number"] as const;
export const AiChartSpec = z.object({
  type: z.enum(AI_CHART_TYPES),
  title: z.string(),
  breakdown: z.string().nullable().optional(),
});
export type AiChartSpec = z.infer<typeof AiChartSpec>;
export const AiEvidence = z.object({
  id: evidenceId,
  tool: z.enum(["query_run", "sql_run"]),
  query_ir: jsonObject.nullable(),
  compiled_sql: z.string(),
  params: jsonObject,
  query_fingerprint: z.string(),
  result_hash: z.string(),
  executed_at: datetime,
  rows: z.number().int().nonnegative(),
  rows_read: z.number().int().nonnegative(),
  /** Chart JSON (query_run: a QueryResult) or `{columns, rows}` (sql_run); may carry `truncated`. */
  result: jsonObject,
  facts: z.record(z.string(), z.number().nullable()),
  sampling: z.object({ factor: z.number().positive().max(1) }),
  ingest_lag_seconds: z.number().int().nonnegative().nullable(),
  date_from: nullableDatetime,
  date_to: nullableDatetime,
  chart: AiChartSpec.nullable(),
  log_comment: z.string(),
});
export type AiEvidence = z.infer<typeof AiEvidence>;
export const CONFIDENCE_RULES = [
  "sample_size",
  "freshness",
  "sampling",
  "catalog_verified",
  "catalog_unknown_event",
  "certified_metric",
  "assumptions",
  "empty_result",
  "no_evidence",
  "outcome",
] as const;
export type ConfidenceRule = (typeof CONFIDENCE_RULES)[number];
export const AiConfidence = z.object({
  score: z.number().min(0).max(1),
  level: z.enum(["high", "medium", "low"]),
  factors: z.array(z.object({ rule: z.enum(CONFIDENCE_RULES), effect: z.number().min(-1).max(1), detail: z.string() })),
});
export type AiConfidence = z.infer<typeof AiConfidence>;
export const AiProposal = z.object({
  kind: z.enum(["save_insight", "draft_alert"]),
  title: z.string(),
  evidence_id: evidenceId.optional(),
  payload: jsonObject,
});
export type AiProposal = z.infer<typeof AiProposal>;
export const AI_OUTCOMES = ["answered", "insufficient_data", "refused", "unverified"] as const;
export type AiOutcome = (typeof AI_OUTCOMES)[number];
/** `AiAnswer` — the final `answer` SSE event, and each entry of a stored conversation. */
export const AiAnswer = z.object({
  answer_id: id,
  conversation_id: id.nullable(),
  question: z.string(),
  outcome: z.enum(AI_OUTCOMES),
  summary: z.string(),
  summary_bindings: z.array(AiBinding),
  claims: z.array(AiClaim),
  evidence: z.array(AiEvidence),
  confidence: AiConfidence,
  assumptions: z.array(z.string()),
  proposals: z.array(AiProposal),
  follow_ups: z.array(z.string()),
  grounding: z.object({ status: z.enum(["passed", "failed"]), attempts: z.number().int(), violations: z.array(z.string()) }),
  usage: z.object({
    model: z.string(),
    steps: z.number().int(),
    input_tokens: z.number().int(),
    output_tokens: z.number().int(),
    cache_read_tokens: z.number().int(),
    cache_write_tokens: z.number().int(),
    cost_micro_usd: z.number().int(),
  }),
  created_at: datetime,
});
export type AiAnswer = z.infer<typeof AiAnswer>;

/** One `POST /ask` SSE payload (`AiAskEvent`); the SSE `event:` name equals `type`. */
export const AskEvent = z.discriminatedUnion("type", [
  z.object({ type: z.literal("conversation"), conversation_id: id, answer_id: id }),
  z.object({ type: z.literal("thinking"), text: z.string() }),
  z.object({
    type: z.literal("tool_call"),
    step: z.number().int(),
    tool: z.string(),
    input: jsonObject,
    evidence_id: evidenceId.optional(),
    compiled_sql: z.string().optional(),
  }),
  z.object({
    type: z.literal("tool_result"),
    step: z.number().int(),
    tool: z.string(),
    ok: z.boolean(),
    summary: z.string(),
    evidence_id: evidenceId.optional(),
  }),
  z.object({ type: z.literal("prose"), text: z.string() }),
  z.object({ type: z.literal("answer"), answer: AiAnswer }),
  z.object({ type: z.literal("error"), code: z.string(), message: z.string() }),
  z.object({ type: z.literal("done") }),
]);
export type AskEvent = z.infer<typeof AskEvent>;

/** GET {region}/ask/conversations → { conversations } (newest first, the caller's own). */
export const AskConversationSummary = z.object({
  id,
  title: z.string().nullable(),
  created_at: datetime,
  updated_at: datetime,
  answers: z.number().int().nonnegative(),
});
export type AskConversationSummary = z.infer<typeof AskConversationSummary>;
export const AskConversationList = z.object({ conversations: z.array(AskConversationSummary) });
/** GET {region}/ask/conversations/{id} → the conversation with its answers (oldest first). */
export const AskConversation = AskConversationSummary.omit({ answers: true }).extend({ answers: z.array(AiAnswer) });
export type AskConversation = z.infer<typeof AskConversation>;
/** 503 codes that mean "Ask is not set up here" rather than "something broke". */
export const AI_UNAVAILABLE_CODES = ["ai_not_configured", "ai_disabled", "ai_storage_unavailable"] as const;

// ── Replays (region: GET /replays, /replays/{id}; scope replays:read) ─────────
export const REPLAYS_SCOPE = "replays:read";
export const REPLAY_PLATFORMS = ["web", "ios", "android", "react-native", "tauri"] as const;
export const ReplayPlatform = z.enum(REPLAY_PLATFORMS);
export type ReplayPlatform = z.infer<typeof ReplayPlatform>;
export const REPLAY_ORDERS = ["start_desc", "start_asc", "duration_desc", "activity_desc", "errors_desc"] as const;
export type ReplayOrder = (typeof REPLAY_ORDERS)[number];

export const ReplaySummary = z.object({
  session_id: z.string(),
  distinct_id: z.string(),
  start: z.string(),
  end: z.string(),
  duration_ms: z.number().nonnegative(),
  active_ms: z.number().nonnegative(),
  clicks: z.number().nonnegative(),
  keypresses: z.number().nonnegative(),
  rage_clicks: z.number().nonnegative(),
  dead_clicks: z.number().nonnegative(),
  console_errors: z.number().nonnegative(),
  errors: z.number().nonnegative(),
  network_failures: z.number().nonnegative(),
  pages: z.number().nonnegative(),
  entry_url: z.string().nullable(),
  urls: z.array(z.string()),
  platform: z.string(),
  mode: z.string(),
  app_version: z.string().nullable(),
  size_bytes: z.number().nonnegative(),
});
export type ReplaySummary = z.infer<typeof ReplaySummary>;
export const ReplaysPage = z.object({
  results: z.array(ReplaySummary),
  limit: z.number().int(),
  offset: z.number().int(),
  has_more: z.boolean(),
});
export type ReplaysPage = z.infer<typeof ReplaysPage>;

/** Filters of GET {region}/replays (ListParams in replays.rs). */
export interface ReplayListParams {
  date_from?: string;
  date_to?: string;
  distinct_id?: string;
  person_id?: string;
  min_duration_ms?: number;
  max_duration_ms?: number;
  has_errors?: boolean;
  min_rage_clicks?: number;
  platform?: ReplayPlatform;
  app_version?: string;
  event?: string;
  order?: ReplayOrder;
  limit?: number;
  offset?: number;
}

/** GET {region}/replays/{session_id} (replay-manifest.schema.json). */
export const ReplayManifestSchema = z.object({
  session_id: z.string(),
  project_data_id: z.number().int().optional(),
  environment: z.string().optional(),
  distinct_id: z.string().optional(),
  platform: ReplayPlatform,
  mode: z.enum(["dom", "wireframe", "screenshot"]),
  first_ts: z.number(),
  last_ts: z.number(),
  blocks: z.array(z.object({ key: z.string(), first_ts: z.number(), last_ts: z.number(), size_bytes: z.number(), url: z.string().optional() })),
  counts: z
    .object({
      clicks: z.number(),
      keypresses: z.number(),
      console_errors: z.number(),
      rage_clicks: z.number(),
      dead_clicks: z.number().optional(),
      errors: z.number().optional(),
      network_failures: z.number().optional(),
    })
    .optional(),
  active_ms: z.number().optional(),
  app_version: z.string().optional(),
  urls: z.array(z.string()).optional(),
  windows: z.array(z.string()).optional(),
});
export type ReplayManifestView = z.infer<typeof ReplayManifestSchema>;

// ── Flags (control plane) ────────────────────────────────────────────────────
// SHAPE: from flags branch — packages/contracts/schemas/flag-definition.schema.json ($defs FlagRule,
// FlagCondition, FlagVariant) and flags-flag.schema.json (Flag, FlagEnvironmentConfig, requests); since
// merged to main (58ea988) and checked against crates/api/src/flags/{dto,routes}.rs there. Routes:
// GET/POST /app/projects/{p}/flags[?archived=&search=], GET/PATCH/DELETE …/flags/{id} (DELETE archives),
// PATCH …/flags/{id}/environments/{environment}. A production config that is enabled before or after a
// change needs `flags.production` (403 `missing_scope`); a taken key is 409 `flag_exists`.
export const FLAG_PRODUCTION_SCOPE = "flags.production";
export const FLAG_KINDS = ["boolean", "multivariate", "remote_config"] as const;
export const FlagKind = z.enum(FLAG_KINDS);
export type FlagKind = z.infer<typeof FlagKind>;
export const FLAG_KEY_RE = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,199}$/;
export const FLAG_OPERATORS = [
  "exact",
  "is_not",
  "in",
  "not_in",
  "icontains",
  "not_icontains",
  "regex",
  "not_regex",
  "gt",
  "gte",
  "lt",
  "lte",
  "is_set",
  "is_not_set",
  "is_date_before",
  "is_date_after",
  "semver_eq",
  "semver_gt",
  "semver_gte",
  "semver_lt",
  "semver_lte",
] as const;
export type FlagOperator = (typeof FLAG_OPERATORS)[number];
const conditionScalar = z.union([z.string(), z.number(), z.boolean()]);
const ConditionValue = z.union([conditionScalar, z.array(conditionScalar).max(1000)]);
export type FlagConditionValue = z.infer<typeof ConditionValue>;
// SHAPE: from flags branch (flag-definition.schema.json $defs/FlagCondition).
export const FlagCondition = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("person_property"),
    key: z.string().min(1, "Pick a property").max(200),
    operator: z.enum(FLAG_OPERATORS),
    value: ConditionValue.optional(),
  }),
  z.object({ type: z.literal("cohort"), cohort_id: id, negate: z.boolean().optional() }),
  z.object({
    type: z.literal("group_property"),
    group_type: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/, "Pick a group type"),
    key: z.string().min(1, "Pick a property").max(200),
    operator: z.enum(FLAG_OPERATORS),
    value: ConditionValue.optional(),
  }),
]);
export type FlagCondition = z.infer<typeof FlagCondition>;
// SHAPE: from flags branch (flag-definition.schema.json $defs/FlagRule).
export const FlagRule = z.object({
  id: z.string().max(64).optional(),
  conditions: z.array(FlagCondition).max(50),
  rollout_percentage: z.number().min(0).max(100),
  variant: z.string().min(1).max(200).optional(),
});
export type FlagRule = z.infer<typeof FlagRule>;
// SHAPE: from flags branch (flag-definition.schema.json $defs/FlagVariant).
export const FlagVariant = z.object({
  key: z.string().min(1, "Name the variant").max(200),
  weight: z.number().int().min(0).max(100),
  payload: z.unknown().optional(),
});
export type FlagVariant = z.infer<typeof FlagVariant>;
const stickiness = z.string().regex(/^(device_id|distinct_id|person_id|group:[A-Za-z0-9_-]{1,64})$/);
// SHAPE: from flags branch (flags-flag.schema.json $defs/FlagEnvironmentConfig).
export const FlagEnvironmentConfig = z.object({
  environment: EnvironmentName,
  enabled: z.boolean(),
  rules: z.array(FlagRule),
  variants: z.array(FlagVariant),
  payload: z.unknown(),
  salt: z.string(),
  stickiness,
  version: z.number().int().min(1),
  updated_at: datetime,
  updated_by: id.nullable(),
});
export type FlagEnvironmentConfig = z.infer<typeof FlagEnvironmentConfig>;
// SHAPE: from flags branch (flags-flag.schema.json, title Flag).
export const Flag = z.object({
  id,
  project_id: id,
  key: z.string().regex(FLAG_KEY_RE),
  name: z.string(),
  description: z.string().nullable(),
  kind: FlagKind,
  tags: z.array(z.string()),
  owner_id: id.nullable(),
  created_by: id.nullable(),
  created_at: datetime,
  updated_at: datetime,
  archived_at: nullableDatetime,
  /** One config per environment, keyed by environment name. */
  environments: z.record(z.string(), FlagEnvironmentConfig),
  experiment_id: id.nullable().optional(),
});
export type Flag = z.infer<typeof Flag>;
// SHAPE: from flags branch ($defs/FlagListResponse).
export const FlagListResponse = z.object({ flags: z.array(Flag) });
// SHAPE: from flags branch ($defs/FlagCreateRequest) → 201 Flag, off everywhere; 409 `flag_exists`.
export const CreateFlagInput = z.object({
  key: z.string().regex(FLAG_KEY_RE, "Use letters, numbers, dots, dashes and underscores, starting with a letter or number"),
  name: z.string().trim().min(1, "Name the flag").max(200),
  description: z.string().trim().max(2000).nullable().optional(),
  kind: FlagKind,
  tags: z.array(z.string()).max(20).optional(),
  rules: z.array(FlagRule).max(100).optional(),
  variants: z.array(FlagVariant).max(20).optional(),
  payload: z.unknown().optional(),
  stickiness: stickiness.optional(),
});
export type CreateFlagInput = z.infer<typeof CreateFlagInput>;
// SHAPE: from flags branch ($defs/FlagUpdateRequest, metadata only).
export const UpdateFlagInput = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  description: z.string().trim().max(2000).nullable().optional(),
  tags: z.array(z.string()).max(20).optional(),
  owner_id: id.nullable().optional(),
});
export type UpdateFlagInput = z.infer<typeof UpdateFlagInput>;
// SHAPE: from flags branch ($defs/FlagEnvironmentUpdateRequest; partial, bumps the version).
export const UpdateFlagEnvironmentInput = z.object({
  enabled: z.boolean().optional(),
  rules: z.array(FlagRule).max(100).optional(),
  variants: z.array(FlagVariant).max(20).optional(),
  payload: z.unknown().optional(),
  salt: z.string().max(64).optional(),
  stickiness: stickiness.optional(),
});
export type UpdateFlagEnvironmentInput = z.infer<typeof UpdateFlagEnvironmentInput>;

// ── Experiments (control plane) ──────────────────────────────────────────────
// SHAPE: from flags branch — packages/contracts/schemas/experiments-experiment.schema.json and
// experiments-stats.schema.json (now on main, crates/api/src/flags/experiments.rs); `analysis` is
// atrix-stats `ExperimentResult` (crates/stats/src/types.rs). Routes: GET/POST /app/projects/{p}/experiments,
// GET/PATCH/DELETE …/experiments/{id}, POST …/{id}/launch | stop | ship, GET …/{id}/results?refresh=,
// POST …/experiments/plan. Errors: 409 `experiment_state` (wrong lifecycle state), `experiment_not_draft`, 400 `no_primary_metric`, `flag_not_multivariate`,
// `flag_in_experiment`, `experiment_blocked` (ship while SRM / guardrail blocked —
// override_block + a reason); 403 `missing_scope` (ship needs experiments.ship AND, in production,
// flags.production; launch in production needs flags.production).
export const EXPERIMENTS_SHIP_SCOPE = "experiments.ship";
export const EXPERIMENT_STATUSES = ["draft", "running", "stopped", "shipped", "archived"] as const;
export const ExperimentStatus = z.enum(EXPERIMENT_STATUSES);
export type ExperimentStatus = z.infer<typeof ExperimentStatus>;
export const EXPERIMENT_KEY_RE = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,99}$/;
export const METRIC_ANALYSIS_KINDS = ["proportion", "mean", "sum", "ratio"] as const;
export const MetricAnalysisKind = z.enum(METRIC_ANALYSIS_KINDS);
export type MetricAnalysisKind = z.infer<typeof MetricAnalysisKind>;
// SHAPE: from flags branch ($defs/ExperimentMetricRef).
export const ExperimentMetricRef = z.object({
  metric_id: id,
  key: z.string(),
  name: z.string(),
  kind: MetricAnalysisKind,
  higher_is_better: z.boolean().optional(),
});
export type ExperimentMetricRef = z.infer<typeof ExperimentMetricRef>;
// SHAPE: from flags branch ($defs/ExperimentGuardrail).
export const ExperimentGuardrail = z.object({
  metric_id: id,
  key: z.string().optional(),
  name: z.string().optional(),
  kind: MetricAnalysisKind.optional(),
  margin: z.number().positive(),
  margin_kind: z.enum(["relative", "absolute"]).optional(),
});
export type ExperimentGuardrail = z.infer<typeof ExperimentGuardrail>;
export const EXPERIMENT_METHODS = ["sequential", "fixed", "bayesian"] as const;
export type ExperimentMethod = (typeof EXPERIMENT_METHODS)[number];
export const ExperimentCuped = z.object({ enabled: z.boolean(), pre_period_days: z.number().int().min(1).max(90) });
// SHAPE: from flags branch (experiments-experiment.schema.json, title Experiment).
export const Experiment = z.object({
  id,
  project_id: id,
  key: z.string(),
  name: z.string(),
  hypothesis: z.string().nullable(),
  status: ExperimentStatus,
  flag_id: id,
  flag_key: z.string(),
  environment: EnvironmentName,
  control_variant: z.string(),
  variants: z.array(FlagVariant),
  exposure_event: z.string(),
  attribution_window_hours: z.number().int(),
  primary_metric: ExperimentMetricRef.nullable(),
  secondary_metrics: z.array(ExperimentMetricRef),
  guardrails: z.array(ExperimentGuardrail),
  method: z.enum(EXPERIMENT_METHODS),
  alpha: z.number(),
  power: z.number(),
  mde: z.number().nullable(),
  cuped: ExperimentCuped.nullable(),
  winsor_pct: z.number().nullable(),
  start_at: nullableDatetime,
  end_at: nullableDatetime,
  shipped_variant: z.string().nullable(),
  decision: jsonObject.nullable(),
  created_by: id.nullable(),
  created_at: datetime,
  updated_at: datetime,
});
export type Experiment = z.infer<typeof Experiment>;
// SHAPE: from flags branch ($defs/ExperimentListResponse).
export const ExperimentListResponse = z.object({ experiments: z.array(Experiment) });
// SHAPE: from flags branch ($defs/ExperimentCreateRequest) → 201 Experiment (draft).
export const CreateExperimentInput = z.object({
  key: z.string().regex(EXPERIMENT_KEY_RE, "Use letters, numbers, dots, dashes and underscores"),
  name: z.string().trim().min(1, "Name the experiment").max(200),
  hypothesis: z.string().trim().max(4000).nullable().optional(),
  flag_id: id,
  environment: EnvironmentName.optional(),
  control_variant: z.string().optional(),
  primary_metric_id: id.nullable().optional(),
  secondary_metric_ids: z.array(id).max(20).optional(),
  guardrails: z.array(ExperimentGuardrail).max(10).optional(),
  method: z.enum(EXPERIMENT_METHODS).optional(),
  alpha: z.number().gt(0).lt(1).optional(),
  power: z.number().gt(0).lt(1).optional(),
  mde: z.number().positive().nullable().optional(),
  cuped: ExperimentCuped.nullable().optional(),
  winsor_pct: z.number().gt(50).lt(100).nullable().optional(),
  attribution_window_hours: z.number().int().min(1).max(8760).optional(),
});
export type CreateExperimentInput = z.infer<typeof CreateExperimentInput>;
// SHAPE: from flags branch ($defs/ExperimentUpdateRequest; non-name fields only while draft).
export const UpdateExperimentInput = CreateExperimentInput.omit({ key: true, flag_id: true, environment: true }).partial();
export type UpdateExperimentInput = z.infer<typeof UpdateExperimentInput>;
/** SHAPE: from flags branch ($defs/ExperimentTransitionRequest). `variant` is required for ship. */
export interface ExperimentTransitionInput {
  reason?: string | null;
  variant?: string;
  override_block?: boolean;
}
export type ExperimentTransition = "launch" | "stop" | "ship";

const Interval = z.object({ lower: z.number(), upper: z.number() });
const Effect = z.object({ estimate: z.number(), ci: Interval });
const ArmSummary = z.object({ n: z.number(), mean: z.number(), adjusted_mean: z.number().nullable() });
export const VERDICTS = ["better", "worse", "inconclusive", "insufficient_data", "blocked_by_srm"] as const;
export type Verdict = (typeof VERDICTS)[number];
export const GUARDRAIL_STATUSES = ["pass", "fail", "inconclusive", "insufficient_data", "blocked_by_srm"] as const;
export type GuardrailStatus = (typeof GUARDRAIL_STATUSES)[number];
/** atrix-stats `ExperimentResult` (crates/stats/src/types.rs, serde snake_case). */
export const ExperimentAnalysis = z.object({
  method: z.enum(["sequential", "fixed_horizon", "bayesian"]),
  alpha: z.number(),
  health: z.object({
    srm: z.object({
      variants: z.array(z.string()),
      observed: z.array(z.number()),
      expected: z.array(z.number()),
      chi_square: z.number(),
      df: z.number(),
      p_value: z.number(),
      flagged: z.boolean(),
    }),
    multi_exposure: z.object({ count: z.number(), rate: z.number(), warning: z.boolean() }),
  }),
  metrics: z.array(
    z.object({
      id: z.string(),
      kind: MetricAnalysisKind,
      role: z.enum(["primary", "secondary", "guardrail"]),
      higher_is_better: z.boolean(),
      control: ArmSummary,
      cuped: z.object({ theta: z.number() }).nullable(),
      variants: z.array(
        z.object({
          variant: z.string(),
          arm: ArmSummary,
          verdict: z.enum(VERDICTS),
          insufficient_reason: z.enum(["too_few_units", "zero_variance", "zero_denominator", "zero_baseline"]).nullable(),
          estimate: z
            .object({
              absolute: Effect,
              relative: Effect.nullable(),
              ci_alpha: z.number(),
              p_value: z.number().nullable(),
              p_value_adjusted: z.number().nullable(),
              chance_to_beat: z.number().nullable(),
              expected_loss: z.object({ ship: z.number(), keep: z.number() }).nullable(),
              cuped_variance_reduction: z.number().nullable(),
              projected_total: Effect.nullable(),
            })
            .nullable(),
          guardrail: z
            .object({
              status: z.enum(GUARDRAIL_STATUSES),
              margin: z.number(),
              margin_kind: z.enum(["relative", "absolute"]),
              bound: z.number().nullable(),
              p_value: z.number().nullable(),
            })
            .nullable(),
        }),
      ),
      progress: z.object({ required_per_variant: z.number(), smallest_variant: z.number(), fraction: z.number() }).nullable(),
    }),
  ),
  blocked: z.boolean(),
});
export type ExperimentAnalysis = z.infer<typeof ExperimentAnalysis>;
export type MetricAnalysis = ExperimentAnalysis["metrics"][number];
export type VariantAnalysis = MetricAnalysis["variants"][number];
// SHAPE: from flags branch (experiments-stats.schema.json $defs/ExperimentArmStats).
export const ArmStats = z.object({
  variant: z.string(),
  n: z.number().int().nonnegative(),
  sum_y: z.number(),
  sum_y2: z.number(),
  covariate: z.object({ sum_x: z.number(), sum_x2: z.number(), sum_xy: z.number() }).nullable().optional(),
  denominator: z.object({ sum_d: z.number(), sum_d2: z.number(), sum_yd: z.number() }).nullable().optional(),
});
export type ArmStats = z.infer<typeof ArmStats>;
// SHAPE: from flags branch (experiments-stats.schema.json $defs/ExperimentStatsResponse).
export const ExperimentStats = z.object({
  exposures: z.array(z.object({ variant: z.string(), units: z.number().int(), multi_exposed: z.number().int() })),
  metrics: z.array(z.object({ id: z.string(), winsor_cap: z.number().nullable().optional(), variants: z.array(ArmStats) })),
  data_through: datetime,
  first_exposure_at: nullableDatetime.optional(),
  last_exposure_at: nullableDatetime.optional(),
  excluded_other_variant: z.number().int().optional(),
});
export type ExperimentStats = z.infer<typeof ExperimentStats>;
// SHAPE: from flags branch ($defs/ExperimentResults): GET …/experiments/{id}/results.
export const ExperimentResults = z.object({
  experiment_id: id,
  computed_at: datetime,
  data_through: datetime,
  stats_version: z.string(),
  stats: ExperimentStats,
  analysis: ExperimentAnalysis,
  metrics: z.record(z.string(), ExperimentMetricRef),
});
export type ExperimentResults = z.infer<typeof ExperimentResults>;
/** SHAPE: from flags branch ($defs/ExperimentPlanRequest). */
export interface ExperimentPlanInput {
  metric: { type: "proportion"; baseline: number } | { type: "continuous"; baseline_mean: number; std_dev: number };
  mde: number;
  mde_kind?: "relative" | "absolute";
  alpha?: number;
  power?: number;
  variants?: number;
  method?: "sequential" | "fixed_horizon" | "bayesian";
  daily_units?: number | null;
}
// SHAPE: from flags branch ($defs/ExperimentPlanResponse; `plan` is atrix-stats Plan).
export const ExperimentPlan = z.object({
  plan: z.object({
    control_units: z.number(),
    treatment_units: z.number(),
    total_units: z.number(),
    days: z.number().nullable().optional(),
    alpha_per_comparison: z.number().optional(),
    mde_absolute: z.number().optional(),
  }),
  mde_at_control_units: z.object({ absolute: z.number(), relative: z.number() }).nullable().optional(),
});
export type ExperimentPlan = z.infer<typeof ExperimentPlan>;
